import React from "react";

/**
 * Renders a forum post / reply body as plain text, but auto-inlines any
 * image URL (`.gif`, `.jpg`, `.jpeg`, `.png`, `.webp`, `.avif`) it finds as
 * a proper `<img>`. Also linkifies bare `http(s)://…` URLs.
 *
 * We deliberately do NOT support arbitrary HTML — the input is still plain
 * text stored in Mongo. This is the smallest surface that unlocks the GIF
 * picker without adding a markdown parser or XSS surface.
 *
 * Props:
 *  - text: string  (the raw body)
 */
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;
const IMG_EXT_RE = /\.(gif|jpe?g|png|webp|avif)(\?.*)?$/i;
// Known image CDNs where the URL may not end in a file extension.
const KNOWN_IMG_HOSTS = /(?:media\d?\.giphy\.com|i\.imgur\.com|cdn\.discordapp\.com\/attachments|media\d?\.tenor\.com|customer-assets\.emergentagent\.com)/i;

function isImageUrl(url) {
  if (!url) return false;
  if (IMG_EXT_RE.test(url)) return true;
  return KNOWN_IMG_HOSTS.test(url);
}

export default function ForumBody({ text, className = "", testId = "forum-body-rendered" }) {
  if (!text) return null;

  const nodes = [];
  let cursor = 0;
  let imgIndex = 0;
  const matches = [...text.matchAll(URL_RE)];

  matches.forEach((m) => {
    const url = m[0];
    const start = m.index;
    if (start > cursor) {
      nodes.push(text.slice(cursor, start));
    }
    if (isImageUrl(url)) {
      nodes.push(
        <span key={`img-${imgIndex}-${start}`} className="block my-2">
          <img
            src={url}
            alt=""
            loading="lazy"
            className="max-w-full max-h-[360px] w-auto h-auto object-contain border border-[var(--t-border)] bg-[#050912]"
          />
        </span>
      );
    } else {
      nodes.push(
        <a
          key={`link-${imgIndex}-${start}`}
          href={url}
          target="_blank"
          rel="noreferrer nofollow"
          className="text-[var(--t-cyan)] hover:text-[var(--t-blue-glow)] underline break-all"
        >
          {url}
        </a>
      );
    }
    imgIndex += 1;
    cursor = start + url.length;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));

  return (
    <div className={`prose-t text-sm md:text-base whitespace-pre-wrap ${className}`} data-testid={testId}>
      {nodes.map((n, i) => (typeof n === "string" ? <React.Fragment key={i}>{n}</React.Fragment> : n))}
    </div>
  );
}
